import DailyIframe, { DailyCall } from '@daily-co/daily-js';
import { ClientManager, ObjectManager } from '../../../concepts/objects';
import { LocalStorageService, StorageService } from '../../../platform/StorageService';
import { BoardInfo } from '../../canvas/BoardInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { ActiveObject, resolve } from '../../Container';
import { PopupManager } from '../../popups/PopupManager';
import { ActiveCallInfo } from './ActiveCallInfo';
import { callDevicePopup } from './CallDevicePopup';
import { CallReference } from './CallReference';
import { DailyApi } from './DailyApi';

export class CallController extends ActiveObject {

    @resolve(ClientManager) private clients!: ClientManager;
    @resolve(LocalStorageService) private localData!: StorageService;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(PopupManager) private popups!: PopupManager;
    @resolve(EditorSession) private session!: EditorSession;

    private api = new DailyApi();
    private call = null as DailyCall;

    public start() {
        this.burden(() => this.call?.destroy());
    }

    public async connect() {

        await this.init();

        if (!await this.configureDevices()) {
            return;
        }

        this.onDailyMeetingJoining();

        const boardInfo = this.session.get(BoardInfo);
        const room = await this.api.getOrCreateRoom(boardInfo.id);

        // If we don't have a call object, create one to tell everyone there is a call!
        if (!this.objects.getAll(CallReference)[0]) {
            this.objects.update(u => {
                const call = u.create(CallReference);
                call.name = room.name;
                call.url = room.url;
            });
        }

        // Invoke the join function
        const result = await this.call.join({ url: room.url });
        if (result) {
            const callerId = result.local.session_id;
            this.clients.self.patch({ callerId });
        }
    }

    public async disconnect() {
        this.clients.self.delete('callerId');
        await this.call.leave();
        await this.call.destroy();
        this.call = null;
    }

    private async init() {
        if (this.call) {
            await this.call.destroy();
        }
        const call = this.call = DailyIframe.createCallObject();
        call.setBandwidth({ trackConstraints: { height: 256 } });
        call.on('error', this.onDailyMeetingStateChange);
        call.on('joined-meeting', this.onDailyMeetingStateChange);
        call.on('left-meeting', this.onDailyMeetingStateChange);
        call.on('participant-joined', this.onDailyParticipantStateChange);
        call.on('participant-updated', this.onDailyParticipantStateChange);
        call.on('participant-left', this.onDailyParticipantStateChange);
    }

    private async configureDevices() {
        const devices: any = await this.call.getInputDevices();
        const audio = devices?.mic?.deviceId ?? this.localData.get('deqo/mediaDevices/audio') ?? null;
        const video = devices?.camera?.deviceId ?? this.localData.get('deqo/mediaDevices/video') ?? null;

        if (audio && video) {
            this.call.setInputDevices({
                audioDeviceId: audio,
                videoDeviceId: video,
            });
        }
        else {
            const { actioned, data } = await this.popups.invoke(callDevicePopup({ audio, video, }));
            if (!actioned) return false;
            this.call.setInputDevices({
                audioDeviceId: data.audio,
                videoDeviceId: data.video,
            });
            this.localData.put('deqo/mediaDevices/audio', data.audio);
            this.localData.put('deqo/mediaDevices/video', data.video);
        }

        return true;
    }

    private onDailyMeetingStateChange = (e: any) => {
        switch (this.call.meetingState()) {
            // case 'error': this.onDailyMeetingJoined(); break;
            case 'joined-meeting': this.onDailyMeetingJoined(); break;
            case 'left-meeting': this.onDailyMeetingLeft(); break;
        }
    };

    private onDailyParticipantStateChange = (e: any) => {
        const entries = Object.entries(this.call.participants());
        const mapped = entries.map(([id, ptpt]) => [id, {
            videoTrack: ptpt.tracks.video,
            audioTrack: ptpt.tracks.audio,
        }]);
        const members = Object.fromEntries(mapped);
        this.session.patch(ActiveCallInfo, { members });
    };

    private onDailyMeetingJoining = () => {
        this.session.patch(ActiveCallInfo, { key: 'connecting' });
    };

    private onDailyMeetingJoined = () => {
        this.session.patch(ActiveCallInfo, { key: 'active' });
    };

    private onDailyMeetingLeft = () => {
        this.session.update(ActiveCallInfo, { key: 'none' });
    };
}
